import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { POPOVER_SURFACE_CLASSES } from '@/components/ui/surface';

export const TOOLTIP_DELAY_MS = 350;

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const tooltipContentVariants = cva(
    'z-50 inline-flex max-w-[min(100vw-1.5rem,20rem)] flex-row flex-wrap items-center gap-[5px] overflow-hidden text-left font-normal [font-feature-settings:"ss03"_on,"ss04"_on,"ss06"_on,"ss08"_on,"liga"_off] animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
    {
        variants: {
            variant: {
                default:
                    'rounded-[2px] bg-grey-50 px-2 py-1.5 text-[12px] leading-[18px] text-black-100 shadow-[0px_4px_12px_-4px_rgba(7,8,10,0.1)]',
                panel: cn(
                    POPOVER_SURFACE_CLASSES,
                    'p-3 text-sm leading-normal text-white-100',
                ),
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

const TooltipContent = React.forwardRef<
    React.ComponentRef<typeof TooltipPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> &
        VariantProps<typeof tooltipContentVariants>
>(
    (
        {
            className,
            variant,
            sideOffset = 4,
            hideWhenDetached = true,
            ...props
        },
        ref,
    ) => (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
                ref={ref}
                data-tooltip-content=""
                sideOffset={sideOffset}
                hideWhenDetached={hideWhenDetached}
                className={cn(tooltipContentVariants({ variant }), className)}
                {...props}
            />
        </TooltipPrimitive.Portal>
    ),
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
